import type { NativeGitActionResult } from './git';

type GitWorkflowDependencies = {
  createBranch: (workspace: string, branch: string) => Promise<void>;
  onBranchCreated?: (branch: string) => void;
  onCommitted?: () => void;
  onPushed?: () => void;
  commit: (input: {
    workspaceRoot: string;
    message: string;
    push: boolean;
  }) => Promise<NativeGitActionResult>;
  push: (workspace: string) => Promise<NativeGitActionResult>;
  createPullRequest: (input: {
    workspaceRoot: string;
    title: string;
    body: string;
    draft: boolean;
  }) => Promise<NativeGitActionResult>;
};

export async function commitWithOptionalBranch(
  input: {
    workspace: string;
    currentBranch: string | null;
    newBranch: string;
    message: string;
    push: boolean;
    commitCompleted?: boolean;
    pushCompleted?: boolean;
  },
  dependencies: Pick<
    GitWorkflowDependencies,
    'createBranch' | 'onBranchCreated' | 'commit' | 'onCommitted' | 'push' | 'onPushed'
  >,
) {
  if (!input.currentBranch?.trim()) {
    const branch = input.newBranch.trim();
    await dependencies.createBranch(input.workspace, branch);
    dependencies.onBranchCreated?.(branch);
  }
  let result: NativeGitActionResult = { pullRequestUrl: null };
  if (input.commitCompleted !== true) {
    result = await dependencies.commit({
      workspaceRoot: input.workspace,
      message: input.message,
      push: false,
    });
    dependencies.onCommitted?.();
  }
  if (input.push && input.pushCompleted !== true) {
    result = await dependencies.push(input.workspace);
    dependencies.onPushed?.();
  }
  return result;
}

export async function createPullRequestWithOptionalBranch(
  input: {
    workspace: string;
    currentBranch: string | null;
    newBranch: string;
    hasChanges: boolean;
    includeLocalChanges: boolean;
    commitMessage: string;
    title: string;
    body: string;
    draft: boolean;
    commitCompleted?: boolean;
    pushCompleted?: boolean;
  },
  dependencies: GitWorkflowDependencies,
) {
  if (!input.currentBranch?.trim()) {
    const branch = input.newBranch.trim();
    await dependencies.createBranch(input.workspace, branch);
    dependencies.onBranchCreated?.(branch);
  }
  if (input.hasChanges && input.includeLocalChanges && input.commitCompleted !== true) {
    await dependencies.commit({
      workspaceRoot: input.workspace,
      message: input.commitMessage.trim() || input.title.trim(),
      push: false,
    });
    dependencies.onCommitted?.();
  }
  if (input.pushCompleted !== true) {
    await dependencies.push(input.workspace);
    dependencies.onPushed?.();
  }
  return dependencies.createPullRequest({
    workspaceRoot: input.workspace,
    title: input.title,
    body: input.body,
    draft: input.draft,
  });
}
